import type {
  DefaultCombinatorName,
  RuleGroupTypeAny,
  ValueProcessorByRule,
  ValueProcessorLegacy,
  DefaultOperatorName,
} from '../../types/index.noReact';

export const numericRegex = /^\s*[+-]?(\d+|\d*\.\d+|\d+\.\d*)([Ee][+-]?\d+)?\s*$/;

export const trimIfString = (val: any) => (typeof val === 'string' ? val.trim() : val);

export const toArray = (v: any) =>
  (Array.isArray(v)
    ? v
    : typeof v === 'string'
    ? v.split(',').filter(s => !/^\s*$/.test(s))
    : []
  ).map(trimIfString);

export const mapSQLOperator = (op: string) => {
  switch (op.toLowerCase()) {
    case 'null':
      return 'is null';
    case 'notnull':
      return 'is not null';
    case 'notin':
      return 'not in';
    case 'notbetween':
      return 'not between';
    case 'contains':
    case 'beginswith':
    case 'endswith':
      return 'like';
    case 'doesnotcontain':
    case 'doesnotbeginwith':
    case 'doesnotendwith':
      return 'not like';
    default:
      return op;
  }
};

export const mongoOperators = {
  '=': '$eq',
  '!=': '$ne',
  '<': '$lt',
  '<=': '$lte',
  '>': '$gt',
  '>=': '$gte',
  in: '$in',
  notIn: '$nin',
};
export const operatorToMongo: Record<string, DefaultOperatorName> = {
  $eq: '=',
  $ne: '!=',
  $lt: '<',
  $lte: '<=',
  $gt: '>',
  $gte: '>=',
  $in: 'in',
  $nin: 'notIn',
  null: 'null',
};

export const celCombinatorMap: Record<DefaultCombinatorName, '&&' | '||'> = {
  and: '&&',
  or: '||',
};

/**
 * Register these operators with jsonLogic before applying the
 * result of formatQuery(query, 'jsonlogic').
 *
 * @example
 * for (const [op, func] of Object.entries(jsonLogicAdditionalOperators)) {
 *   jsonLogic.add_operation(op, func);
 * }
 * jsonLogic.apply({ "startsWith": [{ "var": "firstName" }, "Stev"] }, data);
 */
export const jsonLogicAdditionalOperators: Record<
  'startsWith' | 'endsWith',
  (...args: any[]) => boolean
> = {
  startsWith: (a: string, b: string) => a.startsWith(b),
  endsWith: (a: string, b: string) => a.endsWith(b),
};

export const numerifyValues = (rg: RuleGroupTypeAny): RuleGroupTypeAny => ({
  ...rg,
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore TS can't keep track of odd/even indexes here
  rules: rg.rules.map(r => {
    if (typeof r === 'string') {
      return r;
    } else if ('rules' in r) {
      return numerifyValues(r);
    }
    let { value } = r;
    if (typeof value === 'string' && numericRegex.test(value)) {
      value = parseFloat(value);
    }
    // if (toArray(value).length > 1) {
    //   return { ...r, value };
    // }
    // if (typeof value === 'number' && !isNaN(value)) {
    //   return { ...r, value };
    // }
    return { ...r, value } as RuleGroupTypeAny;
  }),
});

export const isValidValue = (v: any) =>
  (typeof v === 'string' && v.length > 0) ||
  (typeof v === 'number' && !isNaN(v)) ||
  (typeof v !== 'string' && typeof v !== 'number');

export const shouldRenderAsNumber = (v: any, parseNumbers?: boolean) =>
  !!parseNumbers &&
  (typeof v === 'number' ||
    typeof v === 'bigint' ||
    (typeof v === 'string' && numericRegex.test(v)));

export const isValueProcessorLegacy = (
  vp: ValueProcessorLegacy | ValueProcessorByRule
): vp is ValueProcessorLegacy => vp.length >= 3;

export const checkDateString = (dateStr: any) => {
  if (isNaN(dateStr)) {
    const dt = new Date(dateStr);
    if (isNaN(dt.getTime())) {
      return false;
    } else {
      return true;
    }
  } else {
    return false;
  }
};

const regexFieldTypes = [
  'contains',
  'doesNotContain',
  'endsWith',
  'beginsWith',
  'doesNotBeginWith',
  'doesNotEndWith',
  'between',
  'notBetween',
  'in',
  'notIn',
];

export const getManualType = ({
  operator,
  inputType,
  type,
}: {
  operator: string;
  inputType: any;
  type: string;
}): FieldTypeEnum => {
  if (regexFieldTypes.includes(operator)) return FieldTypeEnum.regex;
  if (inputType === 'number') return FieldTypeEnum.number;
  if (inputType === 'objectId') return FieldTypeEnum.objectId;
  if (type === 'checkbox' || type === 'switch') return FieldTypeEnum.boolean;
  // if (type === 'select' || type === 'multiselect') return type;
  // if (type === 'radio') return 'radio';
  return getTypeFromBson(inputType);
};

export enum FieldTypeEnum {
  number = 'number',
  objectId = 'objectId',
  boolean = 'boolean',
  date = 'date',
  regex = 'regex',
  object = 'object',
  array = 'array',
  text = 'text',
}

export const getTypeFromBson = (bsonType: string | number | null | undefined): FieldTypeEnum => {
  switch (bsonType) {
    case 'double':
    case 'Double':
    case 'int':
    case '32-bit integer':
    case 'long':
    case '64-bit integer':
    case 'decimal':
    case 'Decimal128':
    case 'number':
    case 'Number':
    case 1:
    case 16:
    case 18:
    case 19:
      return FieldTypeEnum.number;

    case 'objectId':
    case 'ObjectId':
      return FieldTypeEnum.objectId;

    case 'bool':
    case 'Boolean':
    case 'boolean':
    case 8:
      return FieldTypeEnum.boolean;

    case 'date':
    case 'Date':
    case 9:
      return FieldTypeEnum.date;

    case 'Regular Expression':
    case 'regex':
    case 11:
      return FieldTypeEnum.regex;

    case 'Object':
    case 'object':
    case 3:
      return FieldTypeEnum.object;

    case 'Array':
    case 'array':
    case 4:
      return FieldTypeEnum.array;

    default:
      return FieldTypeEnum.text;
  }
};
